import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Brain, Sparkles, Pin, Trash2, Loader2, Plus, BookOpen, AlertTriangle, Award, Heart, Lightbulb, ScrollText, Zap } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  stakeholder_preference: { label: "Stakeholder Preference", color: "#3B82F6", icon: Heart },
  negotiation_pattern: { label: "Negotiation Pattern", color: "#F59E0B", icon: Lightbulb },
  investor_feedback: { label: "Investor Feedback", color: "#A855F7", icon: ScrollText },
  successful_workflow: { label: "Successful Workflow", color: "#22C55E", icon: Award },
  decision: { label: "Decision", color: "#06B6D4", icon: Zap },
  risk: { label: "Risk", color: "#EF4444", icon: AlertTriangle },
  institutional_fact: { label: "Institutional Fact", color: "#D4AF37", icon: BookOpen },
};

const TYPES = Object.keys(TYPE_META);

export default function Memory() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ type: "institutional_fact", content: "" });

  const load = () => {
    setLoading(true);
    const url = filter ? `/memory/items?type=${filter}` : "/memory/items";
    api.get(url).then((r) => {
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    }).finally(() => setLoading(false));
  };

  useEffect(load, [filter]);

  const extract = async () => {
    setExtracting(true);
    try {
      const r = await api.post("/memory/extract", { source: "all" });
      toast.success(`Extracted ${r.data.extracted} memory items from ${r.data.candidates_processed} sources`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const addItem = async () => {
    if (!newItem.content.trim()) return;
    try {
      await api.post("/memory/items", { ...newItem, pinned: true });
      setNewItem({ type: "institutional_fact", content: "" });
      setAdding(false);
      load();
      toast.success("Memory pinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/memory/items/${id}`);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="memory-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Brain className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
            <span className="label-eyebrow">// Institutional AI Memory</span>
          </div>
          <h1 className="font-display text-3xl text-white tracking-tight">Memory Layer</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Persistent institutional knowledge. The system learns from past meetings, signed proposals, and your
            pinned facts — then surfaces relevant memory to the Copilot at decision time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setAdding(!adding)}
            data-testid="memory-add-toggle"
            className="text-[11px] font-mono tracking-widest px-3 py-2 border border-white/15 text-white hover:border-white/30 transition-colors flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" /> PIN MEMORY
          </button>
          <button
            onClick={extract}
            disabled={extracting}
            data-testid="memory-extract-btn"
            className="text-[11px] font-mono tracking-widest px-3 py-2 transition-all disabled:opacity-60 flex items-center gap-1.5"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
          >
            {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" strokeWidth={2.2} />}
            {extracting ? "EXTRACTING…" : "AUTO-EXTRACT"}
          </button>
        </div>
      </div>

      {/* Add panel */}
      {adding && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="nexus-glass-panel p-5 space-y-3"
          data-testid="memory-add-form"
        >
          <div className="label-eyebrow">// Pin a new memory item</div>
          <select
            value={newItem.type}
            onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
            className="w-full md:w-72 bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none"
            data-testid="memory-type-select"
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>{TYPE_META[t].label}</option>
            ))}
          </select>
          <textarea
            rows={3}
            value={newItem.content}
            onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
            placeholder="E.g., Hon. Minister X prefers concise one-page briefs in Times New Roman 12pt…"
            className="w-full bg-[#0A111F] border border-white/10 text-white text-sm p-3 resize-none focus:border-[#D4AF37]/60 focus:outline-none"
            data-testid="memory-content-input"
          />
          <div className="flex gap-2">
            <button
              onClick={addItem}
              data-testid="memory-save-btn"
              className="text-[11px] font-mono tracking-widest px-3 py-2"
              style={{ background: "var(--gold-500)", color: "#050B14" }}
            >
              PIN
            </button>
            <button
              onClick={() => setAdding(false)}
              className="text-[11px] font-mono tracking-widest px-3 py-2 border border-white/10 text-slate-400 hover:border-white/30"
            >
              CANCEL
            </button>
          </div>
        </motion.div>
      )}

      {/* Counts strip */}
      <div className="flex flex-wrap gap-2" data-testid="memory-type-filter">
        <button
          onClick={() => setFilter("")}
          className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest transition-all ${
            !filter ? "border-[#D4AF37]/60 bg-[#D4AF37]/8 text-white" : "border-white/10 text-slate-300 hover:border-white/25"
          }`}
        >
          ALL <span className="ml-1.5 text-slate-500">{total}</span>
        </button>
        {TYPES.map((t) => {
          const c = counts[t] || 0;
          if (c === 0 && filter !== t) return null;
          return (
            <button
              key={t}
              onClick={() => setFilter(filter === t ? "" : t)}
              data-testid={`filter-${t}`}
              className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest transition-all ${
                filter === t ? "border-[#D4AF37]/60 bg-[#D4AF37]/8 text-white" : "border-white/10 text-slate-300 hover:border-white/25"
              }`}
            >
              <span style={{ color: TYPE_META[t].color }}>●</span>{" "}
              {TYPE_META[t].label.toUpperCase()} <span className="ml-1 text-slate-500">{c}</span>
            </button>
          );
        })}
      </div>

      {/* Items */}
      {loading ? (
        <div className="p-10 text-slate-400 font-mono flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading memory…
        </div>
      ) : items.length === 0 ? (
        <div className="nexus-glass-panel p-10 text-center">
          <Brain className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg text-white">No memory yet</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
            Run <span className="text-[#D4AF37]">AUTO-EXTRACT</span> to mine institutional memory from your meetings
            and signed proposals, or pin a fact manually above.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="memory-items">
          {items.map((m) => {
            const meta = TYPE_META[m.type] || TYPE_META.institutional_fact;
            const Icon = meta.icon;
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="nexus-glass-panel p-4 flex items-start gap-3"
                data-testid={`memory-item-${m.id}`}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center shrink-0"
                  style={{ background: meta.color + "20", border: `1px solid ${meta.color}40` }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-eyebrow" style={{ color: meta.color }}>{meta.label}</span>
                    {m.pinned && <Pin className="w-3 h-3 text-[#D4AF37]" />}
                    <span className="font-mono text-[9px] text-slate-500 tracking-widest ml-auto">
                      {m.source ? m.source.toUpperCase() : ""}
                    </span>
                  </div>
                  <p className="text-sm text-white leading-snug">{m.content}</p>
                  {m.source_label && (
                    <div className="text-[11px] text-slate-500 mt-2 truncate">
                      from: {m.source_label}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(m.id)}
                  className="text-slate-600 hover:text-red-400 p-1"
                  data-testid={`memory-delete-${m.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
